import React from "react";

/**
 * Icon — shared base for the vendored Lucide SVG set.
 *
 * Phase 2 (visual-refresh) sub-step Atoms. Audit §7 + §9.1 chose Lucide;
 * we vendor each icon as a static SVG in `src/icons/<Name>.tsx`. Zero
 * runtime dep, zero bundle weight for unused icons, full editability.
 *
 * Each concrete icon wraps this base, passing its SVG path children.
 * Don't render <Icon /> directly; use one of the named exports.
 *
 * Defaults match Lucide's spec: 24×24 viewBox, 2px stroke, currentColor,
 * round joins. Size defaults to `1em` so the icon scales with the parent's
 * font-size — drop a 14px Info next to body-sm text, a 20px Info in an h2,
 * no manual sizing needed.
 *
 * Accessibility: decorative by default (`aria-hidden="true"`). For
 * meaningful icons, pass `aria-label="..."` and `aria-hidden={undefined}`.
 */

export interface IconProps extends Omit<React.SVGProps<SVGSVGElement>, "children"> {
  /** CSS size. Number → px; string passes through. Defaults to 1em. */
  size?: number | string;
  /** Stroke width in viewBox units. Lucide default is 2; thinner icons use 1.5. */
  strokeWidth?: number;
  /** SVG path content. Passed by the concrete icon component. */
  children: React.ReactNode;
}

export default function Icon({
  size = "1em",
  strokeWidth = 2,
  className = "",
  children,
  ...rest
}: IconProps) {
  const dim = typeof size === "number" ? `${size}px` : size;
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={dim}
      height={dim}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={`v2-icon ${className}`.trim()}
      aria-hidden={rest["aria-label"] ? undefined : true}
      {...rest}
    >
      {children}
    </svg>
  );
}
